/**
 * EL IDIOMA VIVE EN LA DIRECCIÓN WEB.
 *
 * 🔴 EL PROBLEMA (28-ago-2026). El idioma vivía solo en `localStorage`: todas
 * las páginas tenían la MISMA dirección en los 33 idiomas. Las 278 piezas
 * traducidas serían **9.174 páginas con una sola URL**, y Google indexa una.
 * Es decir: todo el trabajo de traducir —5,8 millones de palabras— no lo
 * encontraría nadie por buscador.
 *
 * Y el efecto diario, más inmediato: **no se podía compartir un enlace en un
 * idioma**. Quien mandaba una canalización a un amigo alemán se la mandaba en
 * español, porque el enlace no llevaba el idioma dentro.
 *
 * CÓMO SE RESUELVE SIN TOCAR NINGÚN ENLACE DEL SITIO. React Router acepta un
 * `basename`: si se le da `/de`, TODOS los `to="/canalizaciones"` que ya están
 * escritos se resuelven solos como `/de/canalizaciones`. Los 27 enlaces
 * repartidos por diez archivos siguen tal cual — no hay que reescribir ni uno,
 * y por tanto no hay forma de olvidarse de uno.
 *
 * LA FORMA DE LAS DIRECCIONES:
 *
 *     jeshua.ong/canalizaciones        el idioma por defecto del dominio
 *     jeshua.ong/de/canalizaciones     alemán
 *     jeshua.ong/ja/canalizacion/xxx   japonés
 *
 * ⚠️ EL IDIOMA POR DEFECTO VA SIN PREFIJO, a propósito: así ninguna dirección
 * que ya exista —enlaces compartidos, marcadores, lo que Google tenga indexado—
 * se rompe al desplegar esto. Es un cambio que solo AÑADE direcciones.
 *
 * ⚠️ Y SOLO SE ACEPTAN LOS IDIOMAS QUE EL SITIO OFRECE DE VERDAD. Hoy es uno.
 * `/de/...` no existe todavía y no debe existir: una página en un idioma sin
 * traducir sería otra promesa falsa. Aparecerán solas cuando su idioma pase el
 * umbral de cobertura — ver `idiomas_listos.js`.
 *
 * 🔴 UNA DIRECCIÓN SIRVE SIEMPRE EL MISMO IDIOMA — y esto obligó a cambiar cómo
 * actúa la detección automática.
 *
 * Hasta ahora, `/canalizaciones` mostraba el idioma del navegador de cada
 * visitante: la misma dirección era española para uno y alemana para otro. Con
 * el idioma en la URL eso ya no vale, porque Google visita una vez y guarda lo
 * que le sirvan: indexaría una versión al azar y las etiquetas `hreflang`
 * estarían mintiendo sobre lo que hay en cada dirección.
 *
 * Así que **sin prefijo es siempre el idioma por defecto del dominio**, y la
 * detección por navegador no cambia el contenido: REDIRIGE. Quien llega con el
 * navegador en alemán acaba en `/de/...`, que es su dirección de verdad — la
 * que puede compartir, y la que Google puede indexar como alemana.
 *
 * Se hace una sola vez por visita: si alguien elige español desde el selector
 * estando en alemán, no se le devuelve al alemán en el siguiente clic.
 */

import { getSiteConfig, getSiteLanguages } from './sites';

/**
 * 🔴 EL IDIOMA QUE SE PUEDE SERVIR DE VERDAD (28-ago) — y la razón por la que
 * existe esta función es un fallo que rompió el sitio entero.
 *
 * Al dejar de ofrecer los idiomas sin traducir, `supportedLngs` de i18next pasó
 * a ser solo el español. Pero el idioma POR DEFECTO de jeshua.ong es el inglés,
 * y además cualquiera que hubiera elegido idioma antes lo tenía guardado en su
 * navegador. Resultado: el idioma activo era uno que ya no se sirve, i18next no
 * cargaba ningún texto para él, y las páginas salían con las claves crudas a la
 * vista —«fun.titulo», «home.frase»— en toda la web.
 *
 * En local se veía enseguida; en producción le habría pasado a TODO visitante
 * que hubiera tocado el selector alguna vez.
 *
 * Regla: antes de fijar un idioma, comprobar que se puede servir. Si el del
 * dominio no está disponible, se cae al primero que sí —hoy el español—, y así
 * nunca hay una página sin textos.
 */
export const idiomaServible = (preferido) => {
  const hay = getSiteLanguages();
  if (preferido && hay.includes(preferido)) return preferido;
  const porDefecto = getSiteConfig().defaultLanguage;
  if (hay.includes(porDefecto)) return porDefecto;
  return hay[0] || 'es';
};

/** El idioma que trae la dirección, o null si no trae ninguno. */
export const idiomaDeLaRuta = (ruta = window.location.pathname) => {
  const primero = String(ruta).split('/').filter(Boolean)[0];
  if (!primero) return null;
  return getSiteLanguages().includes(primero) ? primero : null;
};

/** Lo que hay que darle al enrutador: '/de', o '' cuando no hay prefijo. */
export const prefijoDeLaRuta = (ruta = window.location.pathname) => {
  const lng = idiomaDeLaRuta(ruta);
  return lng ? `/${lng}` : '';
};

/** La ruta sin el idioma: de '/de/canalizaciones' devuelve '/canalizaciones'. */
export const rutaSinIdioma = (ruta = window.location.pathname) => {
  const lng = idiomaDeLaRuta(ruta);
  if (!lng) return ruta || '/';
  const resto = ruta.slice(lng.length + 1);
  return resto || '/';
};

/**
 * La dirección completa de esta misma página en otro idioma. Es lo que usa el
 * selector, y también lo que se declara en las etiquetas `hreflang`.
 */
export const direccionEnIdioma = (lng, ruta = window.location.pathname) => {
  const limpia = rutaSinIdioma(ruta);
  const porDefecto = getSiteConfig().defaultLanguage;
  const base = lng === porDefecto ? limpia : `/${lng}${limpia === '/' ? '' : limpia}`;
  return (base || '/') + window.location.search;
};

/**
 * 🔴 EL IDIOMA DE QUIEN TIENE CUENTA ES SUYO, NO DEL NAVEGADOR (Faina, 28-ago:
 * «cuando una persona se registre, de ahí en adelante solo le mostrará ese
 * idioma»).
 *
 * Quien se registra elige idioma al hacerlo, y a partir de ahí ese idioma es un
 * dato DE SU CUENTA: le acompaña entre en el ordenador que entre, y manda sobre
 * lo que pida el navegador y sobre lo que traiga la dirección. Si alguien le
 * comparte `/de/...`, se le lleva a la suya.
 *
 * Es coherente con lo demás que recibe —los correos, el audio, su nombre dentro
 * del texto—: todo eso sale de su ficha, no del ordenador desde el que mire.
 *
 * ⚠️ Y CONVIENE DEJARLO DICHO: esto NO es una medida de seguridad. Nadie entra
 * en una cuenta ajena por el selector de idioma. Lo que protege los datos de
 * quien se registra es otra cosa —autenticación de verdad, HTTPS, no guardar
 * nada sensible en el navegador, no poner datos personales en las direcciones—
 * y hoy el registro todavía es una maqueta (`sesion.js`, `ES_SIMULADO`).
 *
 * Se cambia desde «Mi cuenta», que es donde viven sus datos, no desde el menú.
 */
export const idiomaDelLector = (lector) => {
  if (!lector || !lector.correo) return null;         // sin cuenta, no manda
  const suyo = lector.idioma;
  return suyo && getSiteLanguages().includes(suyo) ? suyo : null;
};

/**
 * ¿Hay que llevar a este visitante a su idioma? Solo si está en una dirección
 * SIN prefijo —o sea, en el idioma por defecto—, no eligió idioma antes, y su
 * navegador pide uno que el sitio sí tiene traducido.
 *
 * Devuelve la dirección a la que ir, o null si se queda donde está.
 */
const STORED_LANGUAGE_KEY = 'jeshua_lang';

const LANGUAGE_ALIASES = { iw: 'he', in: 'id', tl: 'fil', no: 'nb', nn: 'nb' };
const CANTONESE_REGIONS = ['hk', 'mo'];

const readStoredLanguage = () => {
  try {
    return window.localStorage.getItem(STORED_LANGUAGE_KEY) || null;
  } catch (e) {
    return null;
  }
};

const storeLanguage = (lng) => {
  try {
    window.localStorage.setItem(STORED_LANGUAGE_KEY, lng);
  } catch (e) { /* storage unavailable */ }
};

const matchBrowserTag = (tag, available) => {
  const [rawBase, ...parts] = String(tag).toLowerCase().split('-');
  const base = LANGUAGE_ALIASES[rawBase] || rawBase;
  const candidates = [];
  if (base === 'yue' || (base === 'zh' && parts.some((p) => CANTONESE_REGIONS.includes(p)))) {
    candidates.push('yue');
  }
  candidates.push(base);
  return candidates.find((lng) => available.includes(lng)) || null;
};

/** First language the browser asks for that the site offers, or null. */
export const idiomaDelNavegador = (
  requested = window.navigator.languages || [window.navigator.language || ''],
  available = getSiteLanguages()
) => {
  for (const tag of requested) {
    const match = matchBrowserTag(tag, available);
    if (match) return match;
  }
  return null;
};

/**
 * Initial language of a first visit, the single place for the per-domain rule:
 * a reader's stored choice wins; sites with `fixedInitialLanguage` (.cl, .es)
 * open in it; the rest follow the browser and fall back to English.
 */
export const idiomaInicial = () => {
  const available = getSiteLanguages();
  const stored = readStoredLanguage();
  if (stored && available.includes(stored)) return stored;
  const fixed = getSiteConfig().fixedInitialLanguage;
  if (fixed) return available.includes(fixed) ? fixed : null;
  return idiomaDelNavegador(undefined, available) || (available.includes('en') ? 'en' : null);
};

export const destinoPorNavegador = () => {
  if (idiomaDeLaRuta()) return null;
  const wanted = idiomaInicial();
  if (!wanted || wanted === getSiteConfig().defaultLanguage) return null;
  return direccionEnIdioma(wanted);
};

/**
 * Cambiar de idioma es un viaje a otra dirección, no un cambio de estado:
 * se recarga para que el prefijo del enrutador se rehaga entero.
 */
export const irAIdioma = (lng) => {
  storeLanguage(lng);
  window.location.assign(direccionEnIdioma(lng));
};

/**
 * Las etiquetas que le dicen a Google que estas páginas son la misma en otros
 * idiomas. Sin esto, cada traducción parece contenido duplicado y compite
 * contra las demás en vez de sumar.
 *
 * `x-default` apunta al idioma por defecto del dominio: es la versión que se
 * ofrece a quien no encaja en ninguna.
 */
export const publicarHreflang = () => {
  document.querySelectorAll('link[data-hreflang]').forEach((n) => n.remove());
  const idiomas = getSiteLanguages();
  if (idiomas.length < 2) return;   // con un solo idioma no hay nada que declarar
  const origen = window.location.origin;
  const poner = (hl, href) => {
    const l = document.createElement('link');
    l.rel = 'alternate';
    l.hreflang = hl;
    l.href = origen + href;
    l.setAttribute('data-hreflang', '1');
    document.head.appendChild(l);
  };
  idiomas.forEach((lng) => poner(lng, direccionEnIdioma(lng)));
  poner('x-default', direccionEnIdioma(getSiteConfig().defaultLanguage));
};
